import styled from '@emotion/styled';
import { Box, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ReactNode } from 'react';

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}

const Container = styled(Paper)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(2.5, 3),
  border: `1px solid ${theme.palette.divider}`,
  background:
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.24)}, ${alpha(
          theme.palette.common.white,
          0.04
        )})`
      : `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.96)}, ${alpha(
          theme.palette.secondary.main,
          0.16
        )})`,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: -80,
    right: -70,
    width: 220,
    height: 220,
    borderRadius: '50%',
    pointerEvents: 'none',
    background: `radial-gradient(circle, ${alpha(
      theme.palette.primary.main,
      0.24
    )}, transparent 68%)`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: 18,
  },
}));

const Content = styled(Box)`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};

  ${({ theme }) => theme.breakpoints.down('md')} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const TitleBlock = styled(Box)`
  min-width: 0;
`;

const Actions = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};

  ${({ theme }) => theme.breakpoints.down('md')} {
    justify-content: flex-start;
    width: 100%;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    > * {
      flex: 1 1 auto;
    }
  }
`;

const PageHeader = ({ title, subtitle, eyebrow, actions, children }: PageHeaderProps) => (
  <Container elevation={0}>
    <Content>
      <TitleBlock>
        {eyebrow && (
          <Typography
            variant="overline"
            sx={theme => ({
              fontWeight: theme.typography.overline.fontWeight,
              color:
                theme.palette.mode === 'dark'
                  ? theme.palette.secondary.main
                  : theme.palette.primary.main,
            })}
          >
            {eyebrow}
          </Typography>
        )}
        <Typography variant="h5">{title}</Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {children}
      </TitleBlock>
      {actions && <Actions>{actions}</Actions>}
    </Content>
  </Container>
);

export default PageHeader;
